import { DebouncedFuncLeading } from 'lodash'
import { CSSProperties, forwardRef, ReactElement, UIEventHandler, useEffect, useRef } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import { BucketMedia } from '../../../../graphql/generated'
import Thumbnail from './Thumbnail'

interface DataProps {
  items: BucketMedia[]
  current: BucketMedia | null
}

interface Props {
  items: BucketMedia[]
  current: BucketMedia | null
  showThumbs: boolean
  fetchMore: DebouncedFuncLeading<() => Promise<void>>
  hasMore: boolean
}

interface OuterElementProps {
  children: ReactElement
  style: CSSProperties
  onScroll: UIEventHandler<HTMLDivElement>
}

function Column({ index, style, data }: ListChildComponentProps<DataProps>) {
  const { items, current } = data
  const active = items[index]?.id === current?.id

  if (!items[index]) {
    return null
  }

  return (
    <div style={{ ...style, border: active ? '2px solid #000' : '2px solid transparent' }}>
      <Thumbnail media={items[index]} crop={!active} />
    </div>
  )
}

const outerElementType = forwardRef<HTMLDivElement, OuterElementProps>(({ children, style, onScroll }, ref) => (
  <div data-test="thumbnail-list" style={{ ...style, overflowY: 'hidden' }} onScroll={onScroll} ref={ref}>
    {children}
  </div>
))

export default function ThumbnailList({ showThumbs, items, current, fetchMore, hasMore }: Props) {
  const fixedSizeListRef = useRef<FixedSizeList | null>(null)
  const currentIndex = items.findIndex((item) => item.id === current?.id)
  const isItemLoaded = (index: number) => !!items[index]

  useEffect(() => {
    const index = currentIndex === -1 ? 0 : currentIndex
    fixedSizeListRef.current?.scrollToItem(index)
  }, [currentIndex])

  const loadMoreItems = async (_startIndex: number, stopIndex: number) => {
    if (!items[stopIndex]) {
      fetchMore()
    }
  }

  if (!showThumbs) {
    return null
  }

  return (
    <div style={{ position: 'relative', height: 100 }}>
      <AutoSizer>
        {({ width }) => {
          if (!width) return <div />

          return (
            <InfiniteLoader isItemLoaded={isItemLoaded} itemCount={items.length + Number(hasMore)} loadMoreItems={loadMoreItems}>
              {({ onItemsRendered, ref }) => (
                <FixedSizeList
                  ref={(gridRef) => {
                    fixedSizeListRef.current = gridRef
                    ref(gridRef)
                  }}
                  itemData={{ items, current }}
                  height={100}
                  itemCount={items.length}
                  itemSize={100}
                  layout="horizontal"
                  width={width}
                  onItemsRendered={onItemsRendered}
                  outerElementType={outerElementType}
                  initialScrollOffset={currentIndex * 100}
                >
                  {Column}
                </FixedSizeList>
              )}
            </InfiniteLoader>
          )
        }}
      </AutoSizer>
    </div>
  )
}
